import React from "react";
import { useTranslation } from "react-i18next";

/**
 * "From a reply to a table, with no spreadsheet in between." — the section the
 * whole positioning rests on, plus the four beats a guest actually passes
 * through on the way to a chair.
 *
 * Rendered with the site's existing `wps-flow` treatment (introduced by
 * `PhotoSharingQr` for Scan → Join → Share, reused by `RsvpFlow`) rather than a
 * new layout: it is the same kind of thing — a real ordered sequence where each
 * step depends on the one before it — which is why it is an <ol> and why the
 * arrows are decorative (`aria-hidden`) rather than content.
 *
 * The sequence is not a metaphor. Each arrow is a real derivation in the
 * implementation:
 *   - RSVP → guest list: `POST /api/events/:eventId/rsvp/:code` writes the
 *     AttendanceRecord that the guest list reads back
 *     (api/routes/rsvp.routes.mjs).
 *   - guest list → reception guests: `buildReceptionPool`
 *     (api/services/seatingPool.mjs) resolves, on every read, the approved
 *     members of approved guests with an "attending" response to an
 *     RSVP-required reception section. The pool is NEVER stored.
 *   - reception guests → tables: the couple places them. `SeatingAssignment`
 *     rows are written by nothing else (api/routes/seating.routes.mjs), and
 *     seating never writes back onto the guest record.
 *
 * Two things this section may never be reworded into:
 *   1. "syncs" / "updates automatically" — these are derivations on read, and
 *      the couple is the only one who ever writes a seat. A guest who cancels
 *      after being seated KEEPS their chair until it is cleared by hand.
 *   2. "your headcount is your seating list" — `/guests/stats` counts attending
 *      members across ALL sections and applies no `reviewStatus` filter, while
 *      the pool is reception-only and approved-only. The two numbers legitimately
 *      differ, which is what `flow.note` exists to say.
 */
const FLOW_KEYS = ["rsvp", "guestList", "reception", "tables"];

const SeatingFlow = () => {
  const { t } = useTranslation();

  return (
    <section className="section topless wps-section">
      <div className="container">
        <div className="wps-editorial wps-editorial-solo">
          <div className="wps-editorial-text">
            <h2 className="heading-50">{t("weddingSeating.flow.heading")}</h2>
            <p className="body-20">{t("weddingSeating.flow.body1")}</p>
            <p className="body-20">{t("weddingSeating.flow.body2")}</p>
            <p className="wps-note">{t("weddingSeating.flow.note")}</p>
          </div>
        </div>

        <div className="wps-flow" aria-labelledby="wse-flow-label">
          <p id="wse-flow-label" className="wps-eyebrow wps-flow-label">
            {t("weddingSeating.flow.flowLabel")}
          </p>
          <ol className="wps-flow-list">
            {FLOW_KEYS.map((key, index) => (
              <li key={key} className="wps-flow-step">
                <span className="wps-flow-step-txt">
                  {t(`weddingSeating.flow.flow.${key}`)}
                </span>
                {index < FLOW_KEYS.length - 1 ? (
                  <span className="wps-flow-arrow" aria-hidden="true">
                    →
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default SeatingFlow;
